/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

/** Extract form key used to get or set data based on current element schema id
 *
 * @param schemaId The idSchema (e.g. submission_fees_paymentAttributes_0_personal_email)
 * @param depth depth schema level - he many levels in the schema we want to go - e.g 2
 * @returns {null|*} form key e.g fees.paymentAttributes.0
 */
export const getFormKeyBySchemaId = (schemaId, depth) => {
  if (!schemaId) return null;
  const formKey = schemaId.split('_');
  formKey.splice(0, 1); // extract prefix e.g submission

  while (depth > 0) {
    formKey.splice(-1); // extract level elem key
    // eslint-disable-next-line no-plusplus
    depth--;
  }

  return formKey.join('.');
};

export const getCurrentFieldFormKeyBySchemaId = (schemaId) => {
  if (!schemaId) return null;
  const formKey = schemaId.split('_');
  return formKey?.pop();
};

export function guessType(value) {
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  if (value == null) {
    return 'null';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (!Number.isNaN(value)) {
    return 'number';
  }
  if (typeof value === 'object') {
    return 'object';
  }
  // Default to string if we can't figure it out
  return 'string';
}

export function asNumber(value) {
  if (value === '') {
    return undefined;
  }
  if (value === null) {
    return null;
  }
  if (/\.$/.test(value)) {
    // '3.' can't really be considered a number even if it parses in js. The
    // user is most likely entering a float.
    return value;
  }
  if (/\.0$/.test(value)) {
    // we need to return this as a string here, to allow for input like 3.07
    return value;
  }

  if (/\.\d*0$/.test(value)) {
    // It's a number, that's cool - but we need it as a string so it doesn't screw
    // with the user when entering dollar amounts or other values (such as those with
    // specific precision or number of significant digits)
    return value;
  }

  const n = Number(value);
  const valid = typeof n === 'number' && !Number.isNaN(n);

  return valid ? n : value;
}

export function schemaRequiresTrueValue(schema) {
  // Check if const is a truthy value
  if (schema.const) {
    return true;
  }

  // Check if an enum has a single value of true
  if (schema.enum && schema.enum.length === 1 && schema.enum[0] === true) {
    return true;
  }

  // If anyOf has a single value, evaluate the subschema
  if (schema.anyOf && schema.anyOf.length === 1) {
    return schemaRequiresTrueValue(schema.anyOf[0]);
  }

  // If oneOf has a single value, evaluate the subschema
  if (schema.oneOf && schema.oneOf.length === 1) {
    return schemaRequiresTrueValue(schema.oneOf[0]);
  }

  // Evaluate each subschema in allOf, to see if one of them requires a true value
  if (schema.allOf) {
    const schemaSome = subSchema => schemaRequiresTrueValue(subSchema);
    return schema.allOf.some(schemaSome);
  }

  return false;
}
